import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { unifiedInvitationService } from '@/services/unifiedInvitationService';
import { useAuth } from '@/context/AuthContext';

interface UsePostLoginRedirectOptions {
  redirectUrl?: string | null;
  skipPendingCheck?: boolean;
}

export const usePostLoginRedirect = (options: UsePostLoginRedirectOptions = {}) => {
  const { redirectUrl, skipPendingCheck = false } = options;
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Query for pending invitations
  const { data: pendingData, isLoading: checkingPending } = useQuery({
    queryKey: ['pendingInvitations', 'loginCheck'],
    queryFn: unifiedInvitationService.getPendingInvitations,
    enabled: isAuthenticated && !skipPendingCheck,
    staleTime: 1000 * 60, // 1 minute
    retry: false, // Don't retry on error during login flow
  });

  useEffect(() => {
    if (!isAuthenticated) return;

    // If we're skipping the pending check, redirect immediately
    if (skipPendingCheck) {
      if (redirectUrl) {
        navigate(decodeURIComponent(redirectUrl));
      } else {
        navigate('/');
      }
      return;
    }

    // If still checking for pending invitations, wait
    if (checkingPending) return;

    // Check if user has pending project invitations
    const pendingInvitations = pendingData?.invitations || [];
    
    // Filter out global invitations (auto-accept them) and only show project invitations
    const projectInvitations = pendingInvitations.filter(inv => 
      !inv.isGlobalInvite && inv.projects && inv.projects.length > 0
    );

    const hasPendingProjectInvitations = projectInvitations.length > 0;

    if (hasPendingProjectInvitations) {
      // User has pending project invitations, redirect to accept page
      navigate('/accept-invitations');
    } else {
      // No pending project invitations, proceed with normal redirect
      if (redirectUrl) {
        navigate(decodeURIComponent(redirectUrl));
      } else {
        navigate('/');
      }
    }
  }, [isAuthenticated, checkingPending, pendingData, redirectUrl, skipPendingCheck, navigate]);

  return {
    isCheckingPending: checkingPending,
    hasPendingInvitations: pendingData?.invitations?.filter(inv => 
      !inv.isGlobalInvite && inv.projects && inv.projects.length > 0
    ).length > 0,
  };
};
